import React from 'react'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'

import { Button, Flex, Text, Tooltip } from 'components/ds'
import Icon from 'components/ui/icon'

type SeatLimits = {
  current: number
  max: number
  maxAsDisplayString: string
}

type Props = {
  /** Whether the current user is allowed to see the invite action. */
  show: boolean
  /** When true the business cannot invite (over quota, ineligible plan), so the button is disabled. */
  disableInvite: boolean
  /** Where the invite button navigates (invite dialog or seats-upgrade page). */
  inviteModalLink: string
  /** Seat usage, used only for the out-of-seats tooltip copy. */
  usersLimits: SeatLimits
}

/**
 * Builds the out-of-seats tooltip copy, mirroring the on-page seat-quota
 * banner (e.g. "You're using 10 of your 10 seats.").
 */
export const seatUsageLabel = ({
  current,
  max,
  maxAsDisplayString,
}: SeatLimits): string =>
  `You're using ${current} of your ${maxAsDisplayString} ${
    max === 1 ? 'seat' : 'seats'
  }.`

const handleInviteClick = (): void => {
  mixpanel.sendPureEvent('Click button', {
    Context: 'Invite new user',
    Location: 'Users settings',
    Copy: 'Invite',
  })
}

/**
 * Invite call-to-action for the Users settings toolbar.
 *
 * Renders nothing when the user cannot invite, a disabled button with a
 * seat-count tooltip when the business is out of seats, and otherwise an
 * enabled button linking to the invite dialog.
 */
const UsersInviteButton = ({
  show,
  disableInvite,
  inviteModalLink,
  usersLimits,
}: Props): React.ReactNode => {
  if (!show) return null

  const inviteButtonContent = (
    <Flex gap="1" align="center">
      <Icon type="add-user" width={16} height={16} />
      <Text as="span" size="2">
        Invite
      </Text>
    </Flex>
  )

  if (disableInvite) {
    return (
      <Tooltip content={seatUsageLabel(usersLimits)}>
        {/*
          Focusable span: a disabled button emits no pointer events and is
          skipped by keyboard focus, so the wrapper carries the hover target
          and tabIndex to keep the seat-limit tooltip reachable for keyboard
          users. tabIndex on a non-interactive element is intentional here.
        */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <span tabIndex={0}>
          <Button color="gray" highContrast disabled>
            {inviteButtonContent}
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Button asChild color="gray" highContrast>
      <Link to={inviteModalLink} onClick={handleInviteClick}>
        {inviteButtonContent}
      </Link>
    </Button>
  )
}

export default UsersInviteButton
